import { ChatSendPendingError } from '../../../services/modules/chatSendReceipt';
import { useCallback, useRef } from 'react';
import { uazapiService } from '../../../services/uazapiService';
import { chatRepository } from '../../../services/repositories/ChatRepository';
import { ChatMessage, type SendQuotedRef } from './types';
import { createModuleLogger } from '../../../utils/logger';
import { normalizePhoneForChat } from '../../../utils/phoneUtils';
import { sendInstagramTextMessage } from '../../../services/modules/instagramMessagingService';
import { sendWhatsappCloudText } from '../../../services/modules/whatsappCloudService';
import { notify } from '../../../utils/toast';
import {
    type EnqueueChatSend,
    awaitPersistSafe,
    markTempMessageFailed,
    reconcileTempMessageId,
} from '../lib/chatMessageMutations';

const log = createModuleLogger('[ChatTextActions]');

interface UseChatTextActionsProps {
    enqueueSend: EnqueueChatSend;
    instanceName: string | undefined;
    activeConversationId: string | null;
    chatPlatform?: 'whatsapp' | 'instagram';
    messagingConnectionId?: string | null;
    officialApi?: boolean;
    addOptimisticMessage: (msg: ChatMessage) => void;
    replaceOptimisticMessage: (oldId: string, newMsg: ChatMessage) => void;
    removeOptimisticMessage: (msgId: string) => void;
    ensureConversationExists: (text?: string) => Promise<string | null>;
    applySignature?: (text: string) => string;
    currentOrganizationId?: string;
    onSendFailure?: (
        error: unknown,
        text: string,
        quotedMessage?: SendQuotedRef
    ) => void;
    /** Só após envio confirmado — evita auto-assign em falha. */
    onSendSucceeded?: () => void;
}

export const useChatTextActions = ({
    enqueueSend,
    instanceName,
    activeConversationId,
    chatPlatform = 'whatsapp',
    messagingConnectionId,
    officialApi = false,
    addOptimisticMessage,
    replaceOptimisticMessage,
    ensureConversationExists,
    applySignature,
    currentOrganizationId,
    onSendFailure,
    onSendSucceeded,
}: UseChatTextActionsProps) => {

    const onSendFailureRef = useRef(onSendFailure);
    const onSendSucceededRef = useRef(onSendSucceeded);
    onSendSucceededRef.current = onSendSucceeded;
    onSendFailureRef.current = onSendFailure;
    const sendScope = `${currentOrganizationId}:${messagingConnectionId || instanceName}:${activeConversationId}`;
    const activeScopeRef = useRef(sendScope);
    activeScopeRef.current = sendScope;

    const sendMessage = useCallback((
        text: string,
        quotedMessage?: SendQuotedRef
    ) => {
        if (!activeConversationId) {
            log.error('Dados faltando para enviar mensagem');
            return false;
        }
        if (chatPlatform === 'whatsapp' && !officialApi && !instanceName) {
            log.error('Dados faltando para enviar mensagem WhatsApp');
            return false;
        }
        if (chatPlatform === 'whatsapp' && officialApi && !messagingConnectionId) {
            log.error('Dados faltando para enviar mensagem WhatsApp oficial');
            return false;
        }
        if (chatPlatform === 'instagram' && !messagingConnectionId) {
            log.error('Dados faltando para enviar mensagem Instagram');
            return false;
        }

        const signedText = applySignature ? applySignature(text) : text;
        const textLimit = chatPlatform === 'instagram' ? 1000 : officialApi ? 4096 : null;
        if (textLimit && signedText.length > textLimit) {
            notify.error(`A mensagem excede o limite de ${textLimit} caracteres deste canal, incluindo a assinatura.`);
            return false;
        }
        const requestId = crypto.randomUUID();
        const tempId = `temp-${requestId}`;

        const localQuotedMessage = quotedMessage ? {
            id: quotedMessage.id,
            content: quotedMessage.text,
            type: 'text' as const,
            sender: quotedMessage.sender === 'me' ? 'me' as const : 'them' as const
        } : undefined;

        const optimisticMessage: ChatMessage = {
            id: tempId,
            type: 'text',
            text: signedText,
            sender: 'me',
            timestamp: new Date(),
            status: 'pending',
            quotedMessage: localQuotedMessage
        };

        addOptimisticMessage(optimisticMessage);
        log.log('📤 Enviando mensagem...', quotedMessage ? '(com resposta)' : '');

        let convId: string | null = null;
        const persistPromise = (async () => {
            const id = await ensureConversationExists(signedText);
            if (id) {
                await chatRepository.createMessage(id, {
                    ...optimisticMessage,
                    status: 'pending',
                }, currentOrganizationId);
            }
            return id;
        })();
        void persistPromise.catch(() => undefined);

        void enqueueSend(async (onAccepted) => {
            try {
                convId = await persistPromise;
                if (!convId) throw new Error('Não foi possível salvar a conversa antes do envio.');
                let quotedPayload: { id: string; remoteJid: string; fromMe: boolean; text?: string } | undefined;
                if (quotedMessage && !quotedMessage.id.startsWith('temp-')) {
                    quotedPayload = {
                        id: quotedMessage.id,
                        remoteJid: normalizePhoneForChat(activeConversationId),
                        fromMe: quotedMessage.sender === 'me',
                        text: quotedMessage.text
                    };
                }

                const apiTextToSend = signedText;

                const response = chatPlatform === 'instagram'
                    ? await sendInstagramTextMessage({
                        organizationId: currentOrganizationId!,
                        integrationId: messagingConnectionId!,
                        remoteJid: activeConversationId,
                        text: apiTextToSend,
                        requestId, onAccepted,
                    })
                    : officialApi
                    ? await sendWhatsappCloudText({
                        organizationId: currentOrganizationId!,
                        integrationId: messagingConnectionId!,
                        to: activeConversationId,
                        text: signedText,
                        requestId, onAccepted,
                        replyToMessageId: quotedMessage && !quotedMessage.id.startsWith('temp-')
                            ? quotedMessage.id
                            : undefined,
                    })
                    : await uazapiService.sendTextMessage(
                        instanceName!,
                        activeConversationId,
                        apiTextToSend,
                        quotedPayload, requestId, onAccepted,
                    );
                log.log('✅ Mensagem enviada com sucesso');

                const realId = chatPlatform === 'instagram' || officialApi
                    ? (response as { messageId?: string })?.messageId || tempId
                    : (response as { key?: { id?: string }; messageId?: string })?.key?.id
                        || (response as { messageId?: string })?.messageId
                        || tempId;

                replaceOptimisticMessage(tempId, {
                    id: realId,
                    type: 'text',
                    text: signedText,
                    sender: 'me',
                    timestamp: optimisticMessage.timestamp,
                    status: 'sent',
                    quotedMessage: localQuotedMessage,
                });

                convId = await persistPromise;
                await reconcileTempMessageId({
                    conversationId: convId,
                    tempId,
                    realId,
                    peerNotify: currentOrganizationId
                        ? {
                            organizationId: currentOrganizationId,
                            content: signedText,
                            messageType: 'text',
                            messageTimestamp: optimisticMessage.timestamp,
                        }
                        : undefined,
                });
                onSendSucceededRef.current?.();
            } catch (error) {
                log.error('❌ Erro ao enviar mensagem:', error);
                replaceOptimisticMessage(tempId, { ...optimisticMessage, status: error instanceof ChatSendPendingError ? 'uncertain' : 'failed' });
                convId = convId ?? (await awaitPersistSafe(persistPromise));
                await markTempMessageFailed(tempId, convId, error instanceof ChatSendPendingError ? 'uncertain' : 'failed');
                const errMsg = String((error as Error)?.message || '');
                const is503 = errMsg.includes('503');
                const isAuth =
                    /sessão expirou|faça login|não autenticado|mfa|2 etapas|token inválido/i.test(errMsg);
                notify.error(
                    error instanceof ChatSendPendingError ? error.message : isAuth
                        ? errMsg
                        : is503
                          ? 'WhatsApp indisponível no momento. Tente novamente em alguns segundos.'
                          : chatPlatform === 'instagram'
                            ? (errMsg || 'Falha ao enviar no Instagram. Tente novamente.')
                            : officialApi
                              ? (errMsg || 'Falha ao enviar no WhatsApp. Tente novamente.')
                              : 'Falha ao enviar. A mensagem ficou marcada como erro — tente novamente.'
                );
                if (!(error instanceof ChatSendPendingError) && activeScopeRef.current === sendScope) onSendFailureRef.current?.(error, text, quotedMessage);
                if (error instanceof ChatSendPendingError) throw error;
            }
        }).catch(() => undefined);
        return true;
    }, [
        enqueueSend,
        instanceName,
        activeConversationId,
        chatPlatform,
        messagingConnectionId,
        officialApi,
        addOptimisticMessage,
        replaceOptimisticMessage,
        ensureConversationExists,
        applySignature,
        currentOrganizationId,
        sendScope,
    ]);

    return { sendMessage };
};
